import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// ── Register GSAP plugins (idempotent) ────────────────────────────────────
gsap.registerPlugin(TextPlugin, ScrollTrigger);

const {
  FiCpu, FiZap, FiBarChart2, FiMessageSquare,
  FiEye, FiHexagon, FiDatabase, FiArrowRight,
  FiSearch, FiCode, FiActivity, FiLayers
} = FiIcons;

const aiFeatures = [
  {
    icon: FiZap,
    title: "Workflow Automation",
    desc: "Eliminate repetitive manual tasks using intelligent process automation tailored to your existing systems."
  },
  {
    icon: FiBarChart2,
    title: "Predictive Analytics",
    desc: "Use your existing business data to forecast demand, flag anomalies, and surface insights before problems occur."
  },
  {
    icon: FiMessageSquare,
    title: "Natural Language Processing",
    desc: "Build systems that understand text — customer queries, document classification, automated reporting."
  },
  {
    icon: FiEye,
    title: "Computer Vision",
    desc: "Automate visual inspection, object detection, and image-based data extraction for manufacturing and logistics."
  },
  {
    icon: FiHexagon,
    title: "Microsoft Copilot Integration",
    desc: "Embed Microsoft's AI tools into your existing M365 environment to boost team productivity seamlessly."
  },
  {
    icon: FiDatabase,
    title: "Custom ML Models",
    desc: "We design, train, and deploy machine learning models on your own data — built for your specific business problem."
  }
];

const workSteps = [
  { step: "01", title: "Process Audit", desc: "We map your current workflows and identify where AI creates the most value." },
  { step: "02", title: "Proof of Concept", desc: "We build a small, working prototype so you can see results before committing." },
  { step: "03", title: "Full Integration", desc: "The AI feature is built, tested, and integrated directly into your existing system." },
  { step: "04", title: "Monitoring", desc: "We track performance post-launch and retrain models as your data grows." }
];

const industries = ["Manufacturing", "Logistics", "Retail", "Healthcare", "Finance", "Education"];

// ── TextPlugin decoding loop helper ─────────────────────────────────────────
const DECODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ01#@%&!';
const decode = (el, finalText, delay = 0) => {
  if (!el) return;
  const chars = finalText.split('');
  let iteration = 0;
  const maxIterations = chars.length * 3;

  const tl = gsap.timeline({ delay });

  tl.to(el, {
    duration: (maxIterations / 60),
    ease: 'none',
    onUpdate: function () {
      const progress = this.progress();
      const revealed = Math.floor(progress * chars.length);
      el.textContent = chars
        .map((c, i) => {
          if (i < revealed) return c;
          if (c === ' ') return ' ';
          return DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)];
        })
        .join('');
    },
    onComplete: () => {
      el.textContent = finalText;
    },
  });

  return tl;
};

const AIIntegration = () => {
  const navigate = useNavigate();

  // ── Refs ────────────────────────────────────────────────────────────────
  const heroLeftRef = useRef(null);
  const heroRightRef = useRef(null);
  const headlineRef = useRef(null);
  const badgeRef = useRef(null);
  const subRef = useRef(null);
  const problemRef = useRef(null);
  const quoteRef = useRef(null);
  const featureCardsRef = useRef([]);
  const stepsRef = useRef([]);
  const industryTagsRef = useRef([]);
  const ctaRef = useRef(null);

  // ── Collect array refs ──────────────────────────────────────────────────
  const setFeatureRef = (el, i) => { featureCardsRef.current[i] = el; };
  const setStepRef    = (el, i) => { stepsRef.current[i] = el; };
  const setTagRef     = (el, i) => { industryTagsRef.current[i] = el; };

  useEffect(() => {
    const ctx = gsap.context(() => {

      // ── 1. Hero entrance timeline ───────────────────────────────────────
      const heroTl = gsap.timeline({ defaults: { ease: 'expo.out' } });

      heroTl
        .fromTo(badgeRef.current,
          { autoAlpha: 0, y: -12 },
          { autoAlpha: 1, y: 0, duration: 0.7 }
        )
        .fromTo(heroLeftRef.current,
          { autoAlpha: 0, x: -32 },
          { autoAlpha: 1, x: 0, duration: 0.9 },
          '-=0.4'
        )
        .fromTo(heroRightRef.current,
          { autoAlpha: 0, scale: 0.92 },
          { autoAlpha: 1, scale: 1, duration: 0.9 },
          '-=0.6'
        )
        .fromTo(subRef.current,
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.7 },
          '-=0.5'
        );

      // ── 2. TextPlugin "decoding" scramble on the AI headline ────────────
      //    Runs after hero slides in
      heroTl.add(() => {
        decode(headlineRef.current, 'AI Integration for Real Businesses.', 0);
      }, '-=0.2');

      // ── 3. "Problem" section — ScrollTrigger reveal ─────────────────────
      gsap.fromTo(problemRef.current,
        { autoAlpha: 0, y: 40 },
        {
          autoAlpha: 1, y: 0, duration: 1, ease: 'power3.out',
          scrollTrigger: {
            trigger: problemRef.current,
            start: 'top 82%',
            once: true,
          }
        }
      );

      // ── 4. Quote callout — scale + fade ────────────────────────────────
      gsap.fromTo(quoteRef.current,
        { autoAlpha: 0, scale: 0.88 },
        {
          autoAlpha: 1, scale: 1, duration: 1.1, ease: 'expo.out',
          scrollTrigger: {
            trigger: quoteRef.current,
            start: 'top 80%',
            once: true,
          }
        }
      );

      // ── 5. Feature cards — staggered entrance ──────────────────────────
      const cards = featureCardsRef.current.filter(Boolean);
      gsap.fromTo(cards,
        { autoAlpha: 0, y: 36, scale: 0.97 },
        {
          autoAlpha: 1, y: 0, scale: 1,
          duration: 0.65,
          ease: 'power3.out',
          stagger: { amount: 0.45, from: 'start' },
          scrollTrigger: {
            trigger: cards[0],
            start: 'top 84%',
            once: true,
          }
        }
      );

      // ── 6. Process steps — staggered left-to-right ─────────────────────
      const steps = stepsRef.current.filter(Boolean);
      gsap.fromTo(steps,
        { autoAlpha: 0, y: 28 },
        {
          autoAlpha: 1, y: 0,
          duration: 0.6,
          ease: 'power2.out',
          stagger: { amount: 0.35, from: 'start' },
          scrollTrigger: {
            trigger: steps[0],
            start: 'top 86%',
            once: true,
          }
        }
      );

      // ── 7. Industry tags — staggered pop-in ────────────────────────────
      const tags = industryTagsRef.current.filter(Boolean);
      gsap.fromTo(tags,
        { autoAlpha: 0, scale: 0.85, y: 10 },
        {
          autoAlpha: 1, scale: 1, y: 0,
          duration: 0.45,
          ease: 'back.out(1.7)',
          stagger: { amount: 0.3, from: 'center' },
          scrollTrigger: {
            trigger: tags[0],
            start: 'top 88%',
            once: true,
          }
        }
      );

      // ── 8. CTA section ─────────────────────────────────────────────────
      gsap.fromTo(ctaRef.current,
        { autoAlpha: 0, y: 30 },
        {
          autoAlpha: 1, y: 0, duration: 0.9, ease: 'power3.out',
          scrollTrigger: {
            trigger: ctaRef.current,
            start: 'top 85%',
            once: true,
          }
        }
      );

    });

    return () => ctx.revert(); // Clean up all GSAP animations on unmount
  }, []);

  return (
    <div className="pt-32 pb-0 relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

      {/* ── Hero Section ───────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div ref={heroLeftRef} style={{ opacity: 0 }}>
            <div ref={badgeRef} style={{ opacity: 0 }} className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-50 border border-cyan-200 mb-6">
              <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">Specialized Capability</span>
            </div>

            {/* ── Scramble-decoded headline ─────────────────────────── */}
            <h1
              ref={headlineRef}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-8 leading-tight font-mono"
              aria-label="AI Integration for Real Businesses."
            >
              {/* Filled by TextPlugin scramble at runtime */}
            </h1>

            <p ref={subRef} style={{ opacity: 0 }} className="text-slate-600 text-lg leading-relaxed">
              We don't sell AI hype. We build AI features that slot into your existing operations and deliver measurable results.
            </p>
          </div>

          <div ref={heroRightRef} style={{ opacity: 0 }} className="relative">
            <div className="absolute inset-0 bg-cyan-100/40 blur-[100px] rounded-full animate-pulse"></div>
            <div className="relative aspect-square bg-white border border-slate-200 p-12 flex items-center justify-center overflow-hidden shadow-sm">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-full h-full border-[1px] border-cyan-300 transform rotate-45 scale-150"></div>
                <div className="absolute top-0 left-0 w-full h-full border-[1px] border-blue-300 transform -rotate-12 scale-125"></div>
              </div>
              <SafeIcon icon={FiCpu} className="text-8xl text-cyan-500 relative z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* ── The Problem ────────────────────────────────────────────────── */}
      <section className="py-24 bg-slate-50 border-y border-slate-100 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div ref={problemRef} style={{ opacity: 0 }} className="flex flex-col md:flex-row gap-12 items-start">
            <div className="md:w-1/3">
              <div className="flex items-center gap-4 mb-4">
                <SafeIcon icon={FiActivity} className="text-3xl text-cyan-600" />
                <h2 className="text-2xl font-bold text-slate-900 uppercase tracking-wider">The Problem</h2>
              </div>
              <div className="h-1 w-20 bg-cyan-500"></div>
            </div>
            <div className="md:w-2/3">
              <p className="text-slate-700 text-xl leading-relaxed mb-8">
                Most businesses know AI could help them. Few know exactly where to start — or who to trust to implement it properly.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-10">
                Poorly integrated AI creates more confusion than it solves. <span className="text-cyan-600 font-bold">We change that.</span>
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-6 bg-white border border-slate-200 shadow-sm">
                  <div className="text-cyan-500 font-black text-3xl mb-1">90%</div>
                  <div className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Reduction in manual data entry</div>
                </div>
                <div className="p-6 bg-white border border-slate-200 shadow-sm">
                  <div className="text-cyan-500 font-black text-3xl mb-1">4x</div>
                  <div className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Faster anomaly detection</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission Callout ────────────────────────────────────────────── */}
      <section className="py-32 relative z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div ref={quoteRef} style={{ opacity: 0 }} className="relative inline-block">
            <div className="absolute -top-12 -left-12 text-9xl text-cyan-200 font-serif leading-none">"</div>
            <h2 className="text-3xl md:text-6xl font-black text-slate-900 italic relative z-10 px-4">
              We don't sell AI hype — <br />
              <span className="text-gradient-cyan">we engineer outcomes.</span>
            </h2>
            <div className="absolute -bottom-12 -right-12 text-9xl text-cyan-200 font-serif leading-none rotate-180">"</div>
          </div>
        </div>
      </section>

      {/* ── Practical AI Applications ──────────────────────────────────── */}
      <section className="py-24 relative z-10 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Practical AI Applications</h2>
            <p className="text-slate-500 uppercase tracking-widest text-xs font-bold">What we actually build</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiFeatures.map((f, i) => (
              <div
                key={i}
                ref={(el) => setFeatureRef(el, i)}
                style={{ opacity: 0 }}
                className="p-10 bg-white border border-slate-200 hover:border-cyan-300 transition-all group shadow-sm hover:shadow-md"
              >
                <div className="w-12 h-12 bg-cyan-50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={f.icon} className="text-2xl text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{f.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process Timeline ───────────────────────────────────────────── */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">How an AI Integration Typically Works</h2>
            <p className="text-slate-500 uppercase tracking-widest text-xs font-bold">Our process</p>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-[44px] left-0 w-full h-[1px] bg-slate-200"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative z-10">
              {workSteps.map((step, i) => (
                <div
                  key={i}
                  ref={(el) => setStepRef(el, i)}
                  style={{ opacity: 0 }}
                  className="flex flex-col items-center lg:items-start text-center lg:text-left"
                >
                  <div className="w-12 h-12 bg-white border-2 border-slate-200 flex items-center justify-center text-cyan-600 font-black mb-6 shadow-sm">
                    {step.step}
                  </div>
                  <h4 className="text-slate-900 font-bold mb-3">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ─────────────────────────────────────────────────── */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Industries We've Applied AI In</h2>
            <p className="text-slate-500 uppercase tracking-widest text-xs font-bold">Across sectors</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {industries.map((ind, i) => (
              <span
                key={i}
                ref={(el) => setTagRef(el, i)}
                style={{ opacity: 0 }}
                className="px-6 py-3 bg-white border border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-widest hover:border-cyan-300 hover:text-cyan-600 transition-all cursor-default shadow-sm"
              >
                {ind}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-slate-50 border-t border-slate-100 relative z-10">
        <div ref={ctaRef} style={{ opacity: 0 }} className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
            Ready to add intelligence <br /> to your business?
          </h2>
          <p className="text-slate-600 text-lg mb-12 max-w-2xl mx-auto">
            Start with a technical audit. We'll show you exactly where AI can save you time and money.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/book-call')}
              className="w-full sm:w-auto px-10 py-5 bg-cyan-500 text-white font-black uppercase tracking-widest text-xs hover:bg-cyan-400 transition-all flex items-center justify-center gap-2 shadow-sm"
            >
              Book a Discovery Call
              <SafeIcon icon={FiArrowRight} />
            </button>
            <button
              onClick={() => navigate('/services')}
              className="w-full sm:w-auto px-10 py-5 bg-white border border-slate-200 text-slate-700 font-bold uppercase tracking-widest text-xs hover:border-cyan-300 hover:text-cyan-600 transition-all shadow-sm"
            >
              See Our Services
            </button>
          </div>
        </div>
      </section>

      <div className="h-24"></div>
    </div>
  );
};

export default AIIntegration;